import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Injectable, NgZone } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { EMPTY, noop, Observable, throwError } from 'rxjs'
import { catchError, filter } from 'rxjs/operators'
import { appConfig } from '../../../brands/_current/config/app-config'
import { AccountStatus, isAccountStatus } from '../../account/account-status.enum'
import { accountStatusReceived } from '../../account/store/actions'
import { INTERNAL_REDIRECT, REDIRECT_URL_PARAM } from '../../login/shared/login-query-params'
import {
  AccountErrorCodeEnum,
  AuthenticationAdditionalActionEnum,
} from '../../shared/mycicero-web-api/shared/base-login-output.model'
import { isBaseOutput } from '../../shared/mycicero-web-api/shared/base-output.model'
import { fromSignUpFinalizationPage } from '../../sign-up-finalization/shared/sign-up-finalization-params'
import { EmailVerificationMethod } from '../pending-operation-config.model'
import { PendingOperationEnum } from '../pending-operation.enum'
import { PendingData } from './pending-data.model'
import { autoOtpSending, fromSignUpPage } from './pending-operation-params'
import { PendingOperationService } from './pending-operation.service'

@Injectable()
export class PendingOperationInterceptor implements HttpInterceptor {
  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly zone: NgZone,
    private readonly store: Store,
    private readonly pendingOperationService: PendingOperationService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      filter(() => request.url.includes(appConfig.myCiceroWebApi.urls._base)),
      catchError((response) => {
        if (response instanceof HttpErrorResponse && isBaseOutput<AccountErrorCodeEnum>(response.error)) {
          const codeError = response.error.CodeError
          const pendingOperation = response.error.ActionRequired
          if (codeError === AccountErrorCodeEnum.actionRequired && pendingOperation) {
            switch (pendingOperation) {
              case PendingOperationEnum.needConfirmPhone:
                this.needConfirmPhoneActions(response)
                break
              case PendingOperationEnum.needConfirmEmail:
                // TODO: refactor this flow
                /*
                This return statement was added to transfer errors to the component.
                The bug was about not seeing the error message in the component.
                The issue originated from treating this method below as a void method.
                This resulted in the component never entering "catchError" block,
                so never showing error message
                * */
                return this.needConfirmEmailActions(response)
              case PendingOperationEnum.needAcceptRequiredConsents:
                this.needAcceptRequiredConsentsActions()
                break
              case PendingOperationEnum.needChangePassword:
                this.needChangePasswordActions()
                break
              case PendingOperationEnum.needInsertPhone:
                this.needInsertPhoneActions()
                break
              case PendingOperationEnum.needExternalMatching:
                this.needExternalMatchingActions(response)
                break
              case PendingOperationEnum.needThinRegistrationConfirmPhone:
                this.needThinRegistrationConfirmPhoneActions(response)
                break
              case PendingOperationEnum.needThinRegistrationValidateEmail:
                this.needThinRegistrationValidateEmailActions(response)
                break
              case PendingOperationEnum.needThinRegistrationConfirmEmail:
                this.needThinRegistrationConfirmEmailActions(response)
                break
              case PendingOperationEnum.needThinRegistrationGetPassword:
                this.needThinRegistrationGetPasswordActions()
                break
              case PendingOperationEnum.needThinRegistrationCheckPhone:
                this.needThinRegistrationCheckPhoneActions(response)
                break
              case PendingOperationEnum.needInformationSharing:
                this.needInformationSharingActions(response)
                break
            }
            return EMPTY
          }
        }
        return throwError(() => response)
      }),
    )
  }

  private needInformationSharingActions(response: HttpErrorResponse) {
    if (isAccountStatus(response.error.AccountState)) {
      this.store.dispatch(accountStatusReceived({ accountStatus: response.error.AccountState }))
      const check =
        Number(response.error.AccountState) === AccountStatus.active &&
        response.error.AuthenticationAdditionalAction === AuthenticationAdditionalActionEnum.registration
      if (check) {
        this.router
          .navigate([appConfig.signUp.paths._root, appConfig.feedback.paths.success], {
            queryParams: {
              [REDIRECT_URL_PARAM]: this.router.serializeUrl(
                this.router.createUrlTree([
                  appConfig.pendingOperation.paths._root,
                  appConfig.pendingOperation.paths.infoSharing,
                ]),
              ),
              [INTERNAL_REDIRECT]: 1,
            },
            queryParamsHandling: 'merge',
          })
          .then(noop)
      } else {
        this.router
          .navigate([appConfig.pendingOperation.paths._root, appConfig.pendingOperation.paths.infoSharing], {
            queryParamsHandling: 'merge',
          })
          .then(noop)
      }
    } else {
      this.router.navigate([appConfig.feedback.paths._root, appConfig.feedback.paths.genericError]).then(noop)
    }
  }

  private needConfirmPhoneActions(response: HttpErrorResponse) {
    const mobilePendingData: PendingData = {
      mobileNumber: response.error['PhoneNumber'],
      mobileCountryCode: response.error['PhonePrefix'],
    }
    this.pendingOperationService.setPendingData(mobilePendingData)
    const fromSignUp =
      new RegExp(`^/${appConfig.signUp.paths._root}`).test(this.router.url) ||
      this.route.snapshot.queryParams[fromSignUpPage]

    const fromMigration = new RegExp(
      `^/${appConfig.pendingOperation.paths._root}/${appConfig.pendingOperation.paths.migration._root}`,
    ).test(this.router.url)

    const fromConfirmEmail = new RegExp(
      `^/${appConfig.pendingOperation.paths._root}/${appConfig.pendingOperation.paths.confirmEmail}`,
    ).test(this.router.url)

    const fromChangePhone = new RegExp(
      `^/${appConfig.pendingOperation.paths._root}/${appConfig.pendingOperation.paths.changeMobile}`,
    ).test(this.router.url)

    const fromExternalMatching = new RegExp(
      `^/${appConfig.pendingOperation.paths._root}/${appConfig.pendingOperation.paths.externalMatching._root}`,
    ).test(this.router.url)

    this.store.dispatch(accountStatusReceived({ accountStatus: AccountStatus.unconfirmed }))

    if (
      !new RegExp(`^/${appConfig.pendingOperation.paths._root}/${appConfig.pendingOperation.paths.confirmMobile}`).test(
        this.router.url,
      )
    ) {
      const smsDispatchExpiryDate = response.error['DateTimeNextRequestIsAvailable'] ?? null
      if (smsDispatchExpiryDate) {
        this.pendingOperationService.setFormattedSmsDispatchExpiryDate(smsDispatchExpiryDate)
      } else {
        this.pendingOperationService.setSmsDispatchExpiryDate(60)
      }

      const autoOtpSendingParam = { [autoOtpSending]: true }
      if (fromSignUp || fromMigration || fromConfirmEmail || fromChangePhone || fromExternalMatching) {
        const queryParams = {
          ...(fromSignUp ? { [fromSignUpPage]: 1 } : {}),
          ...(fromChangePhone || fromMigration ? {} : autoOtpSendingParam),
        }
        this.zone
          .run(() =>
            this.router.navigate(
              [appConfig.pendingOperation.paths._root, appConfig.pendingOperation.paths.confirmMobile],
              {
                queryParams,
                queryParamsHandling: 'merge',
                state: mobilePendingData,
              },
            ),
          )
          .then(noop)
      } else {
        this.zone
          .run(() =>
            this.router.navigate([appConfig.account.paths._root, appConfig.account.paths.unconfirmed], {
              queryParams: autoOtpSendingParam,
              queryParamsHandling: 'merge',
              state: mobilePendingData,
            }),
          )
          .then(noop)
      }
    }
  }

  private needConfirmEmailActions(response: HttpErrorResponse) {
    const emailPendingData: PendingData = {
      email: response.error['Email'],
    }
    this.pendingOperationService.setPendingData(emailPendingData)
    this.store.dispatch(accountStatusReceived({ accountStatus: AccountStatus.unconfirmed }))
    const confirmEmailRegex =
      appConfig.pendingOperation.emailVerificationMethod === EmailVerificationMethod.magicLinkVerification
        ? new RegExp(`^/${appConfig.pendingOperation.paths._root}/${appConfig.pendingOperation.paths.confirmEmail}`)
        : new RegExp(
            `^/${appConfig.pendingOperation.paths._root}/${appConfig.pendingOperation.paths.externalConfirmEmail}`,
          )
    if (!confirmEmailRegex.test(this.router.url)) {
      this.zone
        .run(() =>
          this.router.navigate(
            [
              appConfig.pendingOperation.paths._root,
              appConfig.pendingOperation.emailVerificationMethod === EmailVerificationMethod.magicLinkVerification
                ? appConfig.pendingOperation.paths.confirmEmail
                : appConfig.pendingOperation.paths.externalConfirmEmail,
            ],
            {
              queryParamsHandling: 'merge',
              state: emailPendingData,
            },
          ),
        )
        .then(noop)
      return EMPTY
    } else {
      /**
       * MWEB-2127 debug-1: upon submitting the confirm-email form the spinner
       * in the continue button (grayed out) remains if the action required needConfirmEmail is returned
       */
      return throwError(() => response)
    }
  }

  private needExternalMatchingActions(response: HttpErrorResponse) {
    this.store.dispatch(accountStatusReceived({ accountStatus: AccountStatus.unconfirmed }))

    const emailPendingData: PendingData = {
      email: response.error['ExternalEmail'],
      mobileNumber: response.error['ExternalPhoneNumber'],
    }
    this.pendingOperationService.setPendingData(emailPendingData)
    this.zone
      .run(() =>
        this.router.navigate(
          [
            appConfig.pendingOperation.paths._root,
            appConfig.pendingOperation.paths.externalMatching._root,
            appConfig.pendingOperation.paths.externalMatching.confirmEmail,
          ],
          {
            queryParamsHandling: 'merge',
            state: emailPendingData,
          },
        ),
      )
      .then(noop)
  }

  private needAcceptRequiredConsentsActions() {
    this.store.dispatch(accountStatusReceived({ accountStatus: AccountStatus.needAcceptConsents }))
    this.zone
      .run(() =>
        this.router.navigate(
          [appConfig.pendingOperation.paths._root, appConfig.pendingOperation.paths.needAcceptConsents],
          {
            queryParamsHandling: 'merge',
          },
        ),
      )
      .then(noop)
  }

  private needChangePasswordActions() {
    this.store.dispatch(accountStatusReceived({ accountStatus: AccountStatus.needChangePassword }))
    this.zone
      .run(() =>
        this.router.navigate(
          [appConfig.pendingOperation.paths._root, appConfig.pendingOperation.paths.changePassword],
          {
            queryParamsHandling: 'merge',
          },
        ),
      )
      .then(noop)
  }

  private needInsertPhoneActions() {
    this.store.dispatch(accountStatusReceived({ accountStatus: AccountStatus.unconfirmed }))
    if (
      !new RegExp(
        `^/${appConfig.pendingOperation.paths._root}/${appConfig.pendingOperation.paths.migration._root}/${appConfig.pendingOperation.paths.migration.needInsertPhone}`,
      ).test(this.router.url)
    ) {
      this.zone
        .run(() =>
          this.router.navigate(
            [
              appConfig.pendingOperation.paths._root,
              appConfig.pendingOperation.paths.migration._root,
              appConfig.pendingOperation.paths.migration.needInsertPhone,
            ],
            {
              queryParamsHandling: 'merge',
            },
          ),
        )
        .then(noop)
    }
  }

  private needThinRegistrationConfirmPhoneActions(response: HttpErrorResponse) {
    const mobilePendingDataThin: PendingData = {
      mobileNumber: response.error['PhoneNumber'],
      mobileCountryCode: response.error['PhonePrefix'],
    }
    this.pendingOperationService.setPendingData(mobilePendingDataThin)
    this.store.dispatch(accountStatusReceived({ accountStatus: AccountStatus.uncompleted }))
    this.pendingOperationService.setFormattedSmsDispatchExpiryDate(response.error['OTPRequestBlockedUntil'])

    this.zone
      .run(() =>
        this.router.navigate(
          [appConfig.signUpFinalization.paths._root, appConfig.signUpFinalization.paths.verifyMobile],
          {
            queryParamsHandling: 'merge',
            state: mobilePendingDataThin,
          },
        ),
      )
      .then(noop)
  }

  private needThinRegistrationValidateEmailActions(response: HttpErrorResponse) {
    const validateEmailData: PendingData = {
      mobileCountryCode: response.error['PhonePrefix'],
      mobileNumber: response.error['PhoneNumber'],
      email: response.error['Email'],
    }
    this.pendingOperationService.setPendingData(validateEmailData)
    this.store.dispatch(accountStatusReceived({ accountStatus: AccountStatus.uncompleted }))

    this.zone
      .run(() =>
        this.router.navigate(
          [appConfig.signUpFinalization.paths._root, appConfig.signUpFinalization.paths.selectEmail],
          {
            queryParamsHandling: 'merge',
            state: validateEmailData,
          },
        ),
      )
      .then(noop)
  }

  private needThinRegistrationConfirmEmailActions(response: HttpErrorResponse) {
    this.store.dispatch(accountStatusReceived({ accountStatus: AccountStatus.uncompleted }))
    this.pendingOperationService.setFormattedEmailDispatchExpiryDate(response.error['OTPEmailRequestBlockedUntil'])
    this.zone
      .run(() =>
        this.router.navigate(
          [appConfig.signUpFinalization.paths._root, appConfig.signUpFinalization.paths.confirmEmailPin],
          {
            queryParamsHandling: 'merge',
          },
        ),
      )
      .then(noop)
  }

  private needThinRegistrationGetPasswordActions() {
    const queryParamsPassword =
      new RegExp(`/${appConfig.signUpFinalization.paths.confirmEmailPin}`).test(this.router.url) ||
      new RegExp(`/${appConfig.signUpFinalization.paths.verifyMobile}`).test(this.router.url)
        ? { [fromSignUpFinalizationPage]: 1 }
        : {}

    this.store.dispatch(accountStatusReceived({ accountStatus: AccountStatus.uncompleted }))
    this.zone
      .run(() =>
        this.router.navigate(
          [appConfig.signUpFinalization.paths._root, appConfig.signUpFinalization.paths.setPassword],
          {
            queryParams: {
              ...queryParamsPassword,
            },
            queryParamsHandling: 'merge',
          },
        ),
      )
      .then(noop)
  }

  private needThinRegistrationCheckPhoneActions(response: HttpErrorResponse) {
    const checkPhoneData: PendingData = {
      mobileCountryCode: response.error['PhonePrefix'],
      mobileNumber: response.error['PhoneNumber'],
      email: response.error['Email'],
      firstName: response.error['Name'],
      lastName: response.error['Surname'],
    }
    this.pendingOperationService.setPendingData(checkPhoneData)
    this.store.dispatch(accountStatusReceived({ accountStatus: AccountStatus.uncompleted }))

    this.zone
      .run(() =>
        this.router.navigate([appConfig.signUpFinalization.paths._root], {
          queryParamsHandling: 'merge',
          state: checkPhoneData,
        }),
      )
      .then(noop)
  }
}
